import { useAuth } from "../../auth/useAuth";
import { getRoleUiForPath } from "../../access/RoleUiCatalogue";
import { Button } from "@akin2unde/flowui-react";
import { Link } from "react-router-dom";
import { RoutePath } from "../../constants/RoutePath";
import { useDashboard } from "./Dashboard";
import type { DashboardPeriod } from "./DashboardState";
import "./DashboardCss.css";

const periods: Array<{ label: string; value: DashboardPeriod }> = [
  { label: "Today", value: "today" },
  { label: "This week", value: "week" },
  { label: "This month", value: "month" },
];

export default function DashboardHTML() {
  const { hasPermission } = useAuth();
  const {
    state,
    snapshot,
    recentOrders,
    lowStockItems,
    userFirstName,
    changePeriod,
    loading,
    error,
    refresh,
    hasData,
    money,
    generatedAt,
  } = useDashboard();

  const chartMax = Math.max(1, ...snapshot.sales.map((x) => x.value));
  return (
    <section className="dashboard-page">
      <header className="dashboard-heading">
        <div>
          <p className="dashboard-eyebrow">Overview</p>
          <h1>Hello, {userFirstName}</h1>
          <p>Here is what is happening with your business today.</p>
        </div>

        <div className="dashboard-heading-actions">
          <Button variant="outline" onClick={refresh} disabled={loading}>
            <i className="fa-solid fa-rotate" aria-hidden="true" /> Refresh
          </Button>
          {hasPermission("Order.View") && (
            <Link to={RoutePath.Orders}>View orders</Link>
          )}
        </div>
      </header>

      <div className="dashboard-period" aria-label="Dashboard period">
        {periods.map((period) => (
          <button
            key={period.value}
            type="button"
            className={
              state.selectedPeriod === period.value
                ? "dashboard-period-active"
                : ""
            }
            onClick={() => changePeriod(period.value)}
          >
            {period.label}
          </button>
        ))}
      </div>

      {loading && <p role="status">Loading dashboard…</p>}
      {error && (
        <div role="alert">
          <p>{error}</p>
          <Button onClick={refresh}>Retry</Button>
        </div>
      )}
      {hasData && (
        <>
          <div className="dashboard-metrics">
            {snapshot.metrics.map((metric) => (
              <article className="dashboard-metric-card" key={metric.label}>
                <div
                  className={`dashboard-metric-icon dashboard-tone-${metric.tone}`}
                >
                  <i className={`fa-solid ${metric.icon}`} aria-hidden="true" />
                </div>
                <div className="dashboard-metric-copy">
                  <span>{metric.label}</span>
                  <strong>{metric.value}</strong>
                  {metric.change && (
                    <small className={`dashboard-change-${metric.direction}`}>
                      <i
                        className={`fa-solid fa-arrow-trend-${metric.direction}`}
                        aria-hidden="true"
                      />
                      {metric.change}
                      <em>vs previous period</em>
                    </small>
                  )}
                </div>
              </article>
            ))}
          </div>

          <div className="dashboard-main-grid">
            <article className="dashboard-card dashboard-sales-card">
              <div className="dashboard-card-header">
                <div>
                  <h2>Sales overview</h2>
                  <p>Revenue performance for the selected period</p>
                </div>
                <span className="dashboard-live-label">
                  <i className="fa-solid fa-circle" aria-hidden="true" />
                  {generatedAt
                    ? `Updated ${new Date(generatedAt).toLocaleTimeString("en-NG")}`
                    : ""}
                </span>
              </div>

              <div
                className="dashboard-chart"
                role="img"
                aria-label="Sales performance chart"
              >
                <div className="dashboard-chart-scale" aria-hidden="true">
                  {[1, 0.75, 0.5, 0.25, 0].map((x) => (
                    <span key={x}>{money.format(chartMax * x)}</span>
                  ))}
                </div>
                <div className="dashboard-chart-bars">
                  {snapshot.sales.every((x) => x.value === 0) && (
                    <p>No sales in this period.</p>
                  )}
                  {snapshot.sales.map((point) => (
                    <div className="dashboard-chart-column" key={point.label}>
                      <div className="dashboard-chart-track">
                        <div
                          className="dashboard-chart-bar"
                          style={{
                            height: `${(point.value / chartMax) * 100}%`,
                          }}
                          title={`${point.label}: ${money.format(point.value)}`}
                        />
                      </div>
                      <span>{point.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </article>

            <article className="dashboard-card dashboard-stock-card">
              <div className="dashboard-card-header">
                <div>
                  <h2>Low stock</h2>
                  <p>Items that need attention</p>
                </div>
                <Link to={RoutePath.Inventory}>View stock</Link>
              </div>

              <div className="dashboard-stock-list">
                {lowStockItems.length === 0 && <p>No low-stock items.</p>}
                {lowStockItems.map((item) => (
                  <div className="dashboard-stock-item" key={item.code}>
                    <div className="dashboard-stock-icon">
                      <i
                        className={`fa-solid ${item.icon}`}
                        aria-hidden="true"
                      />
                    </div>
                    <div className="dashboard-stock-copy">
                      <strong>{item.name}</strong>
                      <span>{item.detail}</span>
                    </div>
                    <div className="dashboard-stock-count">
                      <strong>{item.remaining}</strong>
                      <span>left</span>
                    </div>
                  </div>
                ))}
              </div>

              <Link to={RoutePath.Purchases}>Record a purchase</Link>
            </article>
          </div>

          <div className="dashboard-bottom-grid">
            <article className="dashboard-card dashboard-orders-card">
              <div className="dashboard-card-header">
                <div>
                  <h2>Recent orders</h2>
                  <p>Your latest customer orders</p>
                </div>
                <Link to={RoutePath.Orders}>View all orders</Link>
              </div>

              <div className="dashboard-table-wrap">
                <table className="dashboard-table">
                  <thead>
                    <tr>
                      <th>Order</th>
                      <th>Customer</th>
                      <th>Amount</th>
                      <th>Status</th>
                      <th>Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentOrders.length === 0 && (
                      <tr>
                        <td colSpan={5}>No orders in this period.</td>
                      </tr>
                    )}
                    {recentOrders.map((order) => (
                      <tr key={order.code}>
                        <td>
                          <strong>{order.code}</strong>
                        </td>
                        <td>{order.customer}</td>
                        <td>{order.amount}</td>
                        <td>
                          <span
                            className={`dashboard-status dashboard-status-${order.status.toLowerCase()}`}
                          >
                            {order.status}
                          </span>
                        </td>
                        <td>{order.time}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </article>

            <article className="dashboard-card dashboard-quick-card">
              <div className="dashboard-card-header">
                <div>
                  <h2>Quick actions</h2>
                  <p>Common stock tasks</p>
                </div>
              </div>

              <div className="dashboard-quick-list">
                <QuickAction
                  path={RoutePath.Products}
                  tone="green"
                  icon="fa-box"
                  title="Add product"
                  description="Create a new product listing"
                />
                <QuickAction
                  path={RoutePath.Customers}
                  tone="purple"
                  icon="fa-user-plus"
                  title="Add customer"
                  description="Register a wholesale buyer"
                />
                <QuickAction
                  path={RoutePath.InventoryCounts}
                  tone="orange"
                  icon="fa-warehouse"
                  title="Count stock"
                  description="Record a physical stock count"
                />
              </div>
            </article>
          </div>
        </>
      )}
    </section>
  );
}

interface QuickActionProps {
  path: string;
  tone: string;
  icon: string;
  title: string;
  description: string;
}

function QuickAction({
  path,
  tone,
  icon,
  title,
  description,
}: QuickActionProps) {
  const { hasPermission, state } = useAuth();
  const ui = getRoleUiForPath(path);
  if (
    ui &&
    !(
      hasPermission(`${ui.Code}.Create`) ||
      (state.RoleUis === undefined &&
        hasPermission(
          ui.Legacy.endsWith(".View")
            ? ui.Legacy.replace(/\.View$/, ".Manage")
            : ui.Legacy,
        ))
    )
  )
    return null;
  return (
    <Link to={path}>
      <span className={`dashboard-quick-icon dashboard-tone-${tone}`}>
        <i className={`fa-solid ${icon}`} aria-hidden="true" />
      </span>
      <span>
        <strong>{title}</strong>
        <small>{description}</small>
      </span>
      <i className="fa-solid fa-chevron-right" aria-hidden="true" />
    </Link>
  );
}
